import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createUnit, updateUnit, type UnitOfMeasure } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(16),
  name: z.string().min(1, 'Câmp obligatoriu').max(64),
})
type FormValues = z.infer<typeof schema>

export default function UnitFormDialog({ unit, onClose }: { unit: UnitOfMeasure | null | 'new'; onClose: () => void }) {
  const open = unit !== null
  const editing = unit !== null && unit !== 'new'
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', name: '' },
  })

  useEffect(() => {
    if (editing) {
      const current = unit as UnitOfMeasure
      reset({ code: current.code, name: current.name })
    } else if (unit === 'new') {
      reset({ code: '', name: '' })
    }
  }, [unit, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing) return updateUnit((unit as UnitOfMeasure).id, values)
      return createUnit(values)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['nomenclatoare', 'um'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare UM' : 'UM nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="code" control={control} render={({ field }) => (
              <TextField {...field} label="Cod" disabled={editing} fullWidth
                error={!!errors.code} helperText={errors.code?.message} />
            )} />
            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
